import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {BehaviorSubject, filter, firstValueFrom, Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {DynamicFormService} from '../../dynamic-form/dynamic-form.service';
import {DynamicFormValidationService} from '../dynamic-form-validation.service';
import {ValidationError} from '../validation-error';
import {FieldConfigWrapper} from '../../dynamic-field/field-config-wrapper';
import {AbstractControl, FormControlStatus} from '@angular/forms';
import {EventDispatcherService} from '../../events/event-dispatcher.service';


@UntilDestroy()
@Component({
    selector: 'mydocma-dynamic-field-errors',
    templateUrl: './dynamic-field-errors.component.html',
    styleUrls: ['./dynamic-field-errors.component.scss']
})
export class DynamicFieldErrorsComponent implements OnChanges, OnDestroy, OnInit {

    @Input()
    public fieldWrapper: FieldConfigWrapper<any>;

    @Input()
    public control: AbstractControl;

    private currentErrorSubject: BehaviorSubject<ValidationError> = new BehaviorSubject(null);
    public currentError$ = this.currentErrorSubject.asObservable();

    private subscription: Subscription;
    private error = null;

    constructor(
        private readonly translateService: TranslateService,
        private readonly dynamicFromService: DynamicFormService,
        private readonly dynamicFormValidationService: DynamicFormValidationService,
        private readonly eventDispatcher: EventDispatcherService
    ) {
        this.translateService.onLangChange
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.errorHandler();
            });
    }

    /**
     * A lifecycle hook that is called when any data-bound property of a directive changes.
     *
     * @param changes - changes
     */
    public async ngOnChanges(changes: SimpleChanges) {
        this.subscription?.unsubscribe();

        if (changes.control && changes.control.currentValue) {
            const self = this;
            const origFuncPristine = this.control.markAsPristine;
            this.control.markAsPristine = function () {
                origFuncPristine.apply(this, arguments);

                self.errorHandler();
            };

            const origFuncTouched = this.control.markAsTouched;
            this.control.markAsTouched = function () {
                origFuncTouched.apply(this, arguments);

                self.errorHandler();
            };

            this.subscription = this.control.valueChanges
                .pipe(untilDestroyed(this))
                .subscribe(async () => {
                    await this.errorHanlderWithWaiting();
            });

            await this.errorHanlderWithWaiting();
        }
    }

    /**
     * A lifecycle hook that is called when a directive, pipe, or service is destroyed.
     */
    public ngOnDestroy(): void {
    }

    ngOnInit() {
    }

    async errorHanlderWithWaiting() {
        if (this.control.status === 'PENDING') {
            await firstValueFrom(this.control.statusChanges.pipe(filter(s=> s!=='PENDING')));
        }
        this.errorHandler();
    }

    private errorHandler() {
        const error = this.dynamicFormValidationService.getControlValidationError(this.fieldWrapper);
        if (error) {
            error.errorVisible = this.control.invalid && this.control.touched;
        }
        this.currentErrorSubject.next(error);

        this.checkControlErrors(error);
    }

    private checkControlErrors(error) {
        if (JSON.stringify(this.error) !== JSON.stringify(error)) {
            this.error = error;

            this.eventDispatcher.notifyFieldError({
                config: this.fieldWrapper.config,
                error: this.error
            });
        }
    }
}
